/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */
import { v2, errorToObject } from "@prftesp/cle-logger";
import { v4 as uuid } from "uuid";
import { getConfigValue } from "../../../../sharedServices/configService";
import { PII_FIELDS } from "../../../../utility/constants";
import errorCodes from "../../../../utility/errorCodes";
import { redactPII } from "../../../../utility/helpers";
import {
	getExternalContacts,
	deleteExternalContacts,
	updateExternalContacts,
	getAdAllGroups,
	getAdGroupMembers,
} from "../../services/ExternalContactsService";

const COMPONENT_NAME = "Admin settingsManager/context/externalContacts/actions";

export const actions = {
	FETCH_CONTACTS_STARTED: "FETCH_CONTACTS_STARTED",
	FETCH_CONTACTS_COMPLETED: "FETCH_CONTACTS_COMPLETED",
	FETCH_CONTACTS_ERROR: "FETCH_CONTACTS_ERROR",
	FILTER_CONTACTS: "FILTER_CONTACTS",
	DELETED_CONTACTS: "DELETED_CONTACTS",
	SHOW_DELETE_CONFIRMATION: "SHOW_DELETE_CONFIRMATION",
	CANCEL_DELETE_CONFIRMATION: "CANCEL_DELETE_CONFIRMATION",
	TOGGLE_SELECT_CONTACT: "TOGGLE_SELECT_CONTACT",
	TOGGLE_SELECT_ALL_CONTACTS: "TOGGLE_SELECT_ALL_CONTACTS",
	START_EDIT_CONTACT: "START_EDIT_CONTACT",
	START_NEW_CONTACT: "START_NEW_CONTACT",
	CANCEL_EDIT_CONTACT: "CANCEL_EDIT_CONTACT",
	SAVE_CONTACTS: "SAVE_CONTACTS",
	CREATE_CONTACTS: "CREATE_CONTACTS",
	FETCH_AZURE_USERS_STARTED: "FETCH_AZURE_USERS_STARTED",
	FETCH_AZURE_USERS_COMPLETED: "FETCH_AZURE_USERS_COMPLETED",
	FETCH_AZURE_USERS_ERROR: "FETCH_AZURE_USERS_ERROR",
	FETCH_AZURE_CONFIG_STARTED: "FETCH_AZURE_CONFIG_STARTED",
	FETCH_AZURE_CONFIG_COMPLETED: "FETCH_AZURE_CONFIG_COMPLETED",
};

export const startEditContactAction = (dispatch, { contactId }) => {
	dispatch({ type: actions.START_EDIT_CONTACT, payload: { contactId } });
};

export const startNewContactAction = (dispatch) => {
	dispatch({ type: actions.START_NEW_CONTACT });
};

export const cancelEditContactAction = (dispatch) => {
	dispatch({ type: actions.CANCEL_EDIT_CONTACT });
};

export const saveContactAction = async (dispatch, { contact, showSnackbar }) => {
	let isNewContact = false;
	try {
		if (!contact.contactId) {
			isNewContact = true;
			contact.contactId = uuid();
		}
		await updateExternalContacts([contact]);

		showSnackbar(`Successfully ${isNewContact ? "created" : "saved"} contact.`, "success");
		dispatch({ type: actions.SAVE_CONTACTS, payload: { contact } });
	} catch (error) {
		v2.error(`Error while saving contact`, {
			additionalData: { error: errorToObject(error), contact: redactPII(contact, PII_FIELDS.contact) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.UPDATE_EXTERNAL_CONTACTS_ERROR,
		});
		showSnackbar(`Failed to ${isNewContact ? "create" : "save"} contact.`, "error");
	}
};

export const createContactsAction = async (dispatch, { contacts, showSnackbar }) => {
	try {
		await updateExternalContacts(contacts);

		showSnackbar("Successfully created contacts.", "success");
		dispatch({ type: actions.CREATE_CONTACTS, payload: { contacts } });
	} catch (error) {
		v2.error(`Error while creating contacts`, {
			additionalData: { error: errorToObject(error), contacts: redactPII(contacts, PII_FIELDS.contact) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.SAVE_EXTERNAL_CONTACTS_ERROR,
		});
		showSnackbar("Failed to create contacts.", "error");
	}
};

export const fetchContactsAction = async (dispatch, { showSnackbar }) => {
	try {
		dispatch({ type: actions.FETCH_CONTACTS_STARTED });

		const response = await getExternalContacts();
		const loadedContacts = response?.result || [];

		const contacts = loadedContacts.map((c) => ({ ...c }));

		dispatch({ type: actions.FETCH_CONTACTS_COMPLETED, payload: { contacts } });
	} catch (error) {
		v2.error(`Error while fetching external contacts`, {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.FETCH_EXTERNAL_CONTACTS_ERROR,
		});

		dispatch({ type: actions.FETCH_CONTACTS_ERROR });
		showSnackbar("Failed to load external contacts.", "error");
	}
};

export const showDeleteConfirmationAction = (dispatch, { deleteCount, onConfirm, onCancel }) => {
	dispatch({ type: actions.SHOW_DELETE_CONFIRMATION, payload: { deleteCount, onConfirm, onCancel } });
};

export const cancelDeleteConfirmationAction = (dispatch) => {
	dispatch({ type: actions.CANCEL_DELETE_CONFIRMATION });
};

export const deleteContactsAction = async (dispatch, { showSnackbar, contactIdsToDelete }) => {
	try {
		await deleteExternalContacts(contactIdsToDelete);

		showSnackbar(
			`Successfully removed ${contactIdsToDelete.length > 1 ? contactIdsToDelete.length + " contacts" : "contact"}.`,
			"success",
		);
		dispatch({ type: actions.DELETED_CONTACTS, payload: { contactIdsToDelete } });
	} catch (error) {
		v2.error(`Error while deleting external contacts`, {
			additionalData: { error: errorToObject(error), contactIdsToDelete },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.DELETE_EXTERNAL_CONTACTS_ERROR,
		});
		showSnackbar("Failed to remove contacts.", "error");
	}
};

export const filterContactsAction = (dispatch, { searchTerm }) => {
	dispatch({ type: actions.FILTER_CONTACTS, payload: { searchTerm } });
};

export const toggleSelectContactAction = (dispatch, { contactId }) => {
	dispatch({ type: actions.TOGGLE_SELECT_CONTACT, payload: { contactId } });
};

export const toggleSelectAllContactsAction = (dispatch, { filteredContacts }) => {
	dispatch({ type: actions.TOGGLE_SELECT_ALL_CONTACTS, payload: { filteredContacts } });
};

export const fetchRemoteConfigAction = async (dispatch) => {
	dispatch({ type: actions.FETCH_AZURE_CONFIG_STARTED });
	const { TeamsSyncConfiguration } = await getConfigValue("TeamsSyncConfiguration");
	dispatch({
		type: actions.FETCH_AZURE_CONFIG_COMPLETED,
		payload: { TeamsSyncConfiguration: TeamsSyncConfiguration },
	});
};

// Azure Users Sync Actions started.
export const fetchAzureAdUsersAction = async (dispatch, { isInBackground }) => {
	try {
		if (!isInBackground) {
			dispatch({ type: actions.FETCH_AZURE_USERS_STARTED });
		}

		const groups = await getAdAllGroups();
		let groupMembers = [];
		const groupNames = [];
		for (let idx = 0; idx < groups.length; idx++) {
			const group = groups[idx];
			let members = await getAdGroupMembers(group.id);
			groupNames.push(group.displayName);
			members = members.map((member) => ({ ...member, group: group.displayName }));
			groupMembers = groupMembers.concat(members);
		}

		dispatch({ type: actions.FETCH_AZURE_USERS_COMPLETED, payload: { groupMembers, groupNames } });
	} catch (error) {
		v2.error(`Error while fetching Azure Active Directory users`, {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.AZURE_GET_GROUPS_USERS_ERROR,
		});
		dispatch({
			type: actions.FETCH_AZURE_USERS_ERROR,
			payload: {
				message: error.message,
			},
		});
	}
};
